// ApiClient is the only concrete HttpClient implementation. Instantiated with a
// null token getter so no Authorization header is injected — correct for a local device.
import { ApiClient } from '@/lib/http/ApiClient';
import { CameraError } from '@/lib/errors/AppError';
import { logApiError } from '@/lib/logging/errorLogger';
import type { HttpClient, BaseResponse } from '@/lib/http/HttpClient';
import type {
  KeyenceCameraCommandRequest,
  KeyenceCameraConnectRequest,
  KeyenceCameraDisconnectRequest,
} from './KeyenceCameraRequestType';
import type {
  KeyenceCameraCommandResponse,
  KeyenceCameraConnectResponse,
  KeyenceCameraDisconnectResponse,
} from './KeyenceCameraResponseType';

// Local alias for the Keyence response shape (extends BaseResponse for HttpClient compat)
type KeyenceBaseResponse = BaseResponse & {
  Success: boolean;
  Message?: string;
  Data?: unknown;
};

export class KeyenceCameraApiClient {
  private baseUrl: string;
  private readonly http: HttpClient;

  constructor(baseUrl: string, http?: HttpClient) {
    this.baseUrl = baseUrl;
    this.http = http ?? new ApiClient(baseUrl, () => null, null);
  }

  /**
   * Connect to the Keyence camera
   * @param ipAddress - IP address of the camera (e.g., "192.168.100.100")
   */
  async connect(ipAddress: string): Promise<KeyenceCameraConnectResponse> {
    const request: KeyenceCameraConnectRequest = { ipAddress };

    try {
      const result = await this.http.post<KeyenceBaseResponse>(
        '/api/camera/connect',
        request
      );

      if (!result.ok) {
        const error = new CameraError('Unable to connect to camera', 'keyence', {
          code: 'CAMERA_CONNECTION_ERROR',
          details: { originalError: result.message, ipAddress },
        });
        logApiError(error, '/api/camera/connect', 'POST', {
          component: 'KeyenceCameraApiClient',
          extra: { ipAddress, baseUrl: this.baseUrl },
        });
        throw error;
      }

      return result.data as KeyenceCameraConnectResponse;
    } catch (err) {
      if (err instanceof CameraError) {
        throw err;
      }

      const error = new CameraError('Unable to connect to camera', 'keyence', {
        code: 'CAMERA_CONNECTION_ERROR',
        details: { originalError: (err as Error).message, ipAddress },
      });
      logApiError(error, '/api/camera/connect', 'POST', {
        component: 'KeyenceCameraApiClient',
        extra: { ipAddress, baseUrl: this.baseUrl },
      });
      throw error;
    }
  }

  /**
   * Disconnect from the Keyence camera
   */
  async disconnect(): Promise<KeyenceCameraDisconnectResponse> {
    const request: KeyenceCameraDisconnectRequest = {};

    try {
      const result = await this.http.post<KeyenceBaseResponse>(
        '/api/camera/disconnect',
        request
      );

      if (!result.ok) {
        const error = new CameraError('Unable to disconnect from camera', 'keyence', {
          code: 'CAMERA_DISCONNECTION_ERROR',
          details: { originalError: result.message },
        });
        logApiError(error, '/api/camera/disconnect', 'POST', {
          component: 'KeyenceCameraApiClient',
          extra: { baseUrl: this.baseUrl },
        });
        throw error;
      }

      return result.data as KeyenceCameraDisconnectResponse;
    } catch (err) {
      if (err instanceof CameraError) {
        throw err;
      }

      const error = new CameraError('Unable to disconnect from camera', 'keyence', {
        code: 'CAMERA_DISCONNECTION_ERROR',
        details: { originalError: (err as Error).message },
      });
      logApiError(error, '/api/camera/disconnect', 'POST', {
        component: 'KeyenceCameraApiClient',
        extra: { baseUrl: this.baseUrl },
      });
      throw error;
    }
  }

  /**
   * Send a command to the camera
   * @param command - Command to send (e.g., "LON" to start scanning)
   * @param timeoutMs - Timeout in milliseconds
   */
  async sendCommand(command: string, timeoutMs: number): Promise<KeyenceCameraCommandResponse> {
    const request: KeyenceCameraCommandRequest = { command, timeoutMs };

    try {
      const result = await this.http.post<KeyenceBaseResponse>(
        '/api/camera/command',
        request
      );

      if (!result.ok) {
        const error = new CameraError(
          `Unable to send command "${command}" to camera`,
          'keyence',
          {
            code: 'CAMERA_COMMAND_ERROR',
            details: { originalError: result.message, command, timeoutMs },
          }
        );
        logApiError(error, '/api/camera/command', 'POST', {
          component: 'KeyenceCameraApiClient',
          extra: { command, timeoutMs, baseUrl: this.baseUrl },
        });
        throw error;
      }

      return result.data as KeyenceCameraCommandResponse;
    } catch (err) {
      if (err instanceof CameraError) {
        throw err;
      }

      const error = new CameraError(
        `Unable to send command "${command}" to camera`,
        'keyence',
        {
          code: 'CAMERA_COMMAND_ERROR',
          details: { originalError: (err as Error).message, command, timeoutMs },
        }
      );
      logApiError(error, '/api/camera/command', 'POST', {
        component: 'KeyenceCameraApiClient',
        extra: { command, timeoutMs, baseUrl: this.baseUrl },
      });
      throw error;
    }
  }

  /**
   * Start scanning (sends "LON" command)
   * @param timeoutMs - Timeout in milliseconds (default: 10000)
   */
  async scan(timeoutMs: number = 10000): Promise<KeyenceCameraCommandResponse> {
    return this.sendCommand('LON', timeoutMs);
  }

  /**
   * Cancel ongoing scan (sends "LOFF" command)
   * @param timeoutMs - Timeout in milliseconds (default: 5000)
   */
  async cancelScan(timeoutMs: number = 5000): Promise<KeyenceCameraCommandResponse> {
    return this.sendCommand('LOFF', timeoutMs);
  }

  /**
   * Update the base URL for the API client
   * @param baseUrl - New base URL
   */
  setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
  }

  /**
   * Get the current base URL
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}
